/**
 * Massager detail modal: マッサージ師詳細 + チェックイン機能.
 * Structure and style aligned with design-template modals (AppModal, section headers, table layout).
 */

import React, { useState } from 'react';
import {
  Controller,
  type Control,
  type UseFormClearErrors,
  type UseFormSetValue,
} from 'react-hook-form';
import {
  AppButton,
  AppCard,
  AppForm,
  AppModal,
  AppRadio,
  AppRadioGroup,
  AppSpace,
  AppSpin,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { MASSAGER_GPS_RANGE_OPTIONS } from '@/pages/main/massager/constants';
import type { MassagerClinicLocation } from '@/pages/main/massager/types';
import { useMassagerDetailModalForm } from '@/hooks/main/useMassagerDetailModalForm';
import type { MassagerDetailFormData } from '@/schemas/massagerDetailSchema';
import {
  MASSAGER_DETAIL_FIELD_LABEL_CLASS,
  formatApplicablePeriod,
  formatConsentAlertDisplay,
  formatGpsDisplay,
  getMassagerDetailModalTitle,
  getMassagerLoginLabel,
} from '@/utils/massagerDetailModal';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { useMainMassagerDetail } from '@/hooks/main';

export interface MassagerDetailModalProps {
  massagerId: number | null;
  isOpen: boolean;
  onClose: () => void;
  isA2: boolean;
  checkinEnabled?: boolean;
}

function DetailReadOnlyField(
  label: string,
  value: React.ReactNode,
  truncate2Lines = false
): React.ReactElement {
  return (
    <div key={label} className="pb-2 border-b border-gray-100 min-w-0">
      <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>{label}</div>
      <div className="text-base font-medium text-gray-900 min-h-6">
        {truncate2Lines && (typeof value === 'string' || typeof value === 'number')
          ? renderTableCellTruncate(String(value), { emptyPlaceholder: '' })
          : (value ?? '')}
      </div>
    </div>
  );
}

type CheckinFormControls = {
  control: Control<MassagerDetailFormData>;
  setValue: UseFormSetValue<MassagerDetailFormData>;
  clearErrors: UseFormClearErrors<MassagerDetailFormData>;
  loginLabel: string;
  passwordVisible: boolean;
  setPasswordVisible: (v: boolean | ((p: boolean) => boolean)) => void;
};

function MassagerCheckinEditFields({
  control,
  setValue,
  clearErrors,
  loginLabel,
  passwordVisible,
  setPasswordVisible,
}: CheckinFormControls) {
  return (
    <>
      <div>
        <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>{loginLabel}</div>
        <Controller
          name="login_id"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
              className="mb-0"
            >
              <AppTextField {...field} type="text" autoComplete="off" className="w-full" />
            </AppForm.Item>
          )}
        />
      </div>
      <div>
        <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>パスワード</div>
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
              className="mb-0"
            >
              <AppTextField
                {...field}
                type={passwordVisible ? 'text' : 'password'}
                autoComplete="new-password"
                className="w-full"
                addonAfter={
                  <span className="flex justify-end">
                    <AppButton
                      type="default"
                      htmlType="button"
                      onClick={() => setPasswordVisible(v => !v)}
                      className="shrink-0"
                    >
                      {passwordVisible ? '非表示' : '表示'}
                    </AppButton>
                  </span>
                }
              />
            </AppForm.Item>
          )}
        />
      </div>
      <div className="md:col-span-2">
        <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>同意書切れアラート通知</div>
        <Controller
          name="alertType"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
              className="mb-0"
            >
              <div className="flex flex-col gap-2">
                <AppRadioGroup
                  {...field}
                  value={field.value ?? undefined}
                  onChange={e => {
                    const v = e.target.value as number;
                    field.onChange(v);
                    const opts = { shouldValidate: false } as const;
                    if (v === 1) {
                      setValue('alertDay', '', opts);
                      setValue('alertMonth', '', opts);
                    } else if (v === 2) {
                      setValue('alertMonth', '', opts);
                    } else if (v === 3) {
                      setValue('alertDay', '', opts);
                    }
                    clearErrors(['alertDay', 'alertMonth']);
                  }}
                  className="flex flex-col gap-2"
                >
                  <div className="flex flex-col">
                    <AppRadio value={1}>通知OFF</AppRadio>
                  </div>
                  <div className="flex flex-col">
                    <Controller
                      name="alertDay"
                      control={control}
                      render={({ field: dayField, fieldState }) => (
                        <AppForm.Item
                          validateStatus={fieldState.error ? 'error' : undefined}
                          help={fieldState.error?.message}
                          className="mb-0"
                        >
                          <AppSpace className="items-center flex-wrap">
                            <AppRadio value={2}>
                              <AppTextField
                                {...dayField}
                                onFocus={() => {
                                  field.onChange(2);
                                  setValue('alertMonth', '', { shouldValidate: false });
                                  clearErrors(['alertMonth']);
                                }}
                                placeholder="1～999"
                                className="w-28 text-sm"
                              />
                              <span className="shrink-0 text-gray-700 ml-2">日前</span>
                            </AppRadio>
                          </AppSpace>
                        </AppForm.Item>
                      )}
                    />
                  </div>
                  <div className="flex flex-col">
                    <Controller
                      name="alertMonth"
                      control={control}
                      render={({ field: monthField, fieldState }) => (
                        <AppForm.Item
                          validateStatus={fieldState.error ? 'error' : undefined}
                          help={fieldState.error?.message}
                          className="mb-0"
                        >
                          <AppSpace className="items-center flex-wrap">
                            <AppRadio value={3}>
                              <AppTextField
                                {...monthField}
                                onFocus={() => {
                                  field.onChange(3);
                                  setValue('alertDay', '', { shouldValidate: false });
                                  clearErrors(['alertDay']);
                                }}
                                placeholder="1～99"
                                className="w-28 text-sm"
                              />
                              <span className="shrink-0 text-gray-700 ml-2">ヶ月前</span>
                            </AppRadio>
                          </AppSpace>
                        </AppForm.Item>
                      )}
                    />
                  </div>
                </AppRadioGroup>
              </div>
            </AppForm.Item>
          )}
        />
      </div>
      <div className="md:col-span-2">
        <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>GPS範囲</div>
        <Controller
          name="gpsRange"
          control={control}
          render={({ field, fieldState }) => (
            <AppForm.Item
              validateStatus={fieldState.error ? 'error' : undefined}
              help={fieldState.error?.message}
              className="mb-0"
            >
              <AppRadioGroup
                value={field.value ?? undefined}
                onChange={e => field.onChange(e.target.value as number)}
                className="mt-2 flex gap-4 flex-wrap"
              >
                {MASSAGER_GPS_RANGE_OPTIONS.map(o => (
                  <AppRadio key={o.value} value={o.value}>
                    {o.label}
                  </AppRadio>
                ))}
              </AppRadioGroup>
            </AppForm.Item>
          )}
        />
      </div>
    </>
  );
}

function MassagerPasswordReadRow({
  massager,
  passwordVisible,
  setPasswordVisible,
}: {
  massager: MassagerClinicLocation | null;
  passwordVisible: boolean;
  setPasswordVisible: (v: boolean | ((p: boolean) => boolean)) => void;
}) {
  const raw = massager?.massager?.password;
  const display = passwordVisible && raw ? raw : raw ? '*'.repeat(raw.length) : '';
  return (
    <div
      className="pb-2 border-b border-gray-100 last:border-0 last:pb-0"
      style={{ gridColumn: 'span 1' }}
    >
      <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>パスワード</div>
      <div className="flex items-center justify-between gap-2 min-w-0">
        {renderTableCellTruncate(display, {
          className: 'text-base font-medium text-gray-900 break-all flex-1 text-left',
          emptyPlaceholder: '',
        })}
        {raw && (
          <AppButton
            type="default"
            htmlType="button"
            onClick={() => setPasswordVisible(v => !v)}
            className="shrink-0"
          >
            {passwordVisible ? '非表示' : '表示'}
          </AppButton>
        )}
      </div>
    </div>
  );
}

const MassagerDetailModal: React.FC<MassagerDetailModalProps> = ({
  massagerId,
  isOpen,
  onClose,
  isA2,
  checkinEnabled = false,
}) => {
  const {
    data: detailResponse,
    isLoading: detailLoading,
    isFetching: detailFetching,
    isError: isDetailError,
    error: detailError,
  } = useMainMassagerDetail(isOpen ? massagerId ?? 0 : 0);

  const massager: MassagerClinicLocation | null = detailResponse?.data ?? null;
  const showDetailLoading = (detailLoading || detailFetching) && !isDetailError;

  const {
    form,
    passwordVisible,
    setPasswordVisible,
    isEditMode,
    setIsEditMode,
    updateLoading,
    handleClose,
    handleCancelEdit,
    onSubmit,
  } = useMassagerDetailModalForm({ massager, isOpen, checkinEnabled, onClose });

  const { control, handleSubmit, setValue, clearErrors } = form;

  const [readPasswordVisible, setReadPasswordVisible] = useState(false);

  if (!isOpen || !massagerId) return null;

  const loginLabel = getMassagerLoginLabel(isA2);
  const modalTitle = getMassagerDetailModalTitle(isEditMode, isA2);

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-end gap-3">
          {isEditMode ? (
            <>
              <AppButton type="default" disabled={updateLoading} onClick={handleCancelEdit}>
                キャンセル
              </AppButton>
              <AppButton
                type="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={updateLoading}
                loading={updateLoading}
                className="hover:opacity-90"
              >
                {updateLoading ? '更新中' : '保存'}
              </AppButton>
            </>
          ) : checkinEnabled ? (
            <AppButton
              disabled={showDetailLoading}
              type="primary"
              onClick={() => setIsEditMode(true)}
              className="hover:opacity-90"
            >
              編集
            </AppButton>
          ) : (
            null
          )}
        </div>
      }
    >
      <div className="relative min-h-[200px]">
        {showDetailLoading && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-white/70 backdrop-blur-sm"
            aria-busy="true"
            aria-label="読み込み中"
          >
            <AppSpin size="large" />
          </div>
        )}
        {isDetailError && !showDetailLoading && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
            <AppTypography.P className="mb-3">
              {detailError?.message || 'データの取得に失敗しました。'}
            </AppTypography.P>
            <div className="flex justify-end">
              <AppButton type="primary" onClick={onClose}>
                閉じる
              </AppButton>
            </div>
          </div>
        )}
        <AppCard title="基本情報" isHeaderColor className="mb-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {DetailReadOnlyField('施術師名', massager?.massager?.name, true)}
            {DetailReadOnlyField(
              '郵便番号',
              massager?.clinic_location?.postal_code
                ? `〒${massager?.clinic_location.postal_code}`
                : null
            )}
            <div className="md:col-span-2">
              {DetailReadOnlyField('住所', massager?.clinic_location?.full_address, true)}
            </div>
            <div className="md:col-span-2">
              {DetailReadOnlyField('適用期間', formatApplicablePeriod(massager))}
            </div>
            {DetailReadOnlyField('電話番号', massager?.clinic_location?.telephone)}
            {DetailReadOnlyField('FAX', massager?.clinic_location?.fax)}
            {isA2 ? (
              <>
                {DetailReadOnlyField(
                  'はり免許番号',
                  massager?.massager?.acupuncture_license_number
                )}
                {DetailReadOnlyField(
                  'きゅう免許番号',
                  massager?.massager?.moxibustion_license_number
                )}
              </>
            ) : (
              DetailReadOnlyField('免許番号', massager?.massager?.license_number)
            )}
            {DetailReadOnlyField(
              '保健所登録区分',
              massager?.clinic_location?.health_center_regist_type?.name
            )}
            {isEditMode && checkinEnabled ? (
              <div key="email">
                <div className={MASSAGER_DETAIL_FIELD_LABEL_CLASS}>メールアドレス</div>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppTextField {...field} type="email" autoComplete="off" className="w-full" />
                    </AppForm.Item>
                  )}
                />
              </div>
            ) : (
              DetailReadOnlyField('メールアドレス', massager?.massager?.email)
            )}
          </div>
        </AppCard>

        {checkinEnabled && (
          <AppCard title="チェックイン機能" isHeaderColor className="mb-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {isEditMode ? (
                <MassagerCheckinEditFields
                  control={control}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  loginLabel={loginLabel}
                  passwordVisible={passwordVisible}
                  setPasswordVisible={setPasswordVisible}
                />
              ) : (
                <>
                  {DetailReadOnlyField(loginLabel, massager?.massager?.login_id)}
                  <MassagerPasswordReadRow
                    massager={massager || null}
                    passwordVisible={readPasswordVisible}
                    setPasswordVisible={setReadPasswordVisible}
                  />
                  {DetailReadOnlyField(
                    '同意書切れアラート通知',
                    formatConsentAlertDisplay(massager)
                  )}
                  {DetailReadOnlyField('GPS範囲', formatGpsDisplay(massager))}
                </>
              )}
            </div>
          </AppCard>
        )}
      </div>
    </AppModal>
  );
};

export default MassagerDetailModal;
